import React, { useState } from 'react';
import Papa from 'papaparse';
import { masterSupabase as supabase } from '../../lib/supabase-master';

const PREVIEW_LIMIT = 250;

export default function RentCsvUpload({ onClose, onSuccess }) {
  const [preview, setPreview] = useState([]);
  const [errors, setErrors] = useState([]);
  const [summary, setSummary] = useState({ total: 0, sum: 0, previewCount: 0 });
  const [busy, setBusy] = useState(false);

  // ============================================================
  // PHASE 1 FIX: Robuste Header-Normalisierung
  // ============================================================
  
  /**
   * Normalisiert Header für zuverlässige Erkennung
   * Entfernt: Leerzeichen, Slashes, Bindestriche, Underscores
   * Beispiel: "Auftraggeber/Empfänger" → "auftraggeberempfänger"
   */
  const normalizeHeader = (header) => {
    return header.toLowerCase().replace(/\s|\/|-|_/g, '');
  };

  /**
   * Findet Spalte mit erweiterter Pattern-Erkennung
   */
  const findColumn = (headers, patterns) => {
    return headers.findIndex(h => {
      const normalized = normalizeHeader(h);
      return patterns.some(pattern => pattern.test(normalized));
    });
  };

  // ============================================================
  // CSV PARSING
  // ============================================================
  
  async function parseFile(file) {
    setBusy(true);
    try {
      const text = await file.text();
      
      Papa.parse(text, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          const headers = result.meta.fields || [];
          
          // PHASE 1 FIX: Erweiterte Spalten-Erkennung für VR Bank & andere Banken
          const dateCol = findColumn(headers, [
            /datum|date|buchung|valuta/i
          ]);
          
          const amountCol = findColumn(headers, [
            /betrag|amount|summe|umsatz/i
          ]);
          
          // WICHTIG: Erweiterte Name-Erkennung (VR Bank: "Name Zahlungsbeteiligter")
          const senderNameCol = findColumn(headers, [
            /zahlungsbeteiligter|auftraggeber|empfänger|begünstigter|zahlungspflichtiger/i
          ]);
          
          const ibanCol = findColumn(headers, [
            /ibanzahlungsbeteiligter/i  // VR Bank: "IBAN Zahlungsbeteiligter"
          ]);
          
          const refCol = findColumn(headers, [
            /verwendungszweck|verwendung/i  // VR Bank: "Verwendungszweck" (NOT "Buchungstext"!)
          ]);

          const parsed = [];
          const errs = [];

          result.data.forEach((row, idx) => {
            try {
              const dateStr = row[headers[dateCol]] || '';
              const amountStr = row[headers[amountCol]] || '';
              const senderName = row[headers[senderNameCol]] || '';
              const senderIban = row[headers[ibanCol]] || '';
              const reference = row[headers[refCol]] || '';

              // Parse date (DD.MM.YYYY, YYYY-MM-DD, DD/MM/YYYY)
              let date;
              if (dateStr.includes('-')) {
                date = new Date(dateStr);
              } else if (dateStr.includes('/')) {
                const dateParts = dateStr.split('/');
                if (dateParts.length === 3) {
                  date = new Date(`${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`);
                }
              } else {
                const dateParts = dateStr.split('.');
                if (dateParts.length === 3) {
                  date = new Date(`${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`);
                }
              }
              if (!date || isNaN(date)) throw new Error(`Invalid date format: ${dateStr}`);

              // Parse amount (handle thousand separators and decimal commas)
              let cleanAmountStr = amountStr.replace(/[^\d,.-]/g, '');
              const lastDot = cleanAmountStr.lastIndexOf('.');
              const lastComma = cleanAmountStr.lastIndexOf(',');
              
              if (lastComma > lastDot) {
                // Comma is decimal separator: 1.234,56 -> 1234.56
                cleanAmountStr = cleanAmountStr.replace(/\./g, '').replace(',', '.');
              } else if (lastDot > lastComma) {
                // Dot is decimal separator: 1,234.56 -> 1234.56
                cleanAmountStr = cleanAmountStr.replace(/,/g, '');
              } else {
                // Only one type of separator or none
                cleanAmountStr = cleanAmountStr.replace(',', '.');
              }
              
              const amount = parseFloat(cleanAmountStr);
              if (isNaN(amount)) throw new Error(`Invalid amount: ${amountStr}`);

              parsed.push({
                transaction_date: date.toISOString().split('T')[0],
                amount,
                sender_name: senderName.trim(),
                sender_iban: senderIban.trim(),
                reference: reference.trim()
              });
            } catch (e) {
              errs.push({ row: idx + 2, error: e.message }); // +2 for header + 1-indexed
            }
          });

          setPreview(parsed);
          setErrors(errs);
          setSummary({
            total: parsed.length,
            sum: parsed.reduce((acc, r) => acc + r.amount, 0),
            previewCount: Math.min(parsed.length, PREVIEW_LIMIT)
          });
        },
        error: (error) => {
          throw new Error(`CSV Parse Error: ${error.message}`);
        }
      });
    } catch (e) {
      alert(e?.message || 'Parsing fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  }

  // ============================================================
  // UPLOAD TO DATABASE
  // ============================================================
  
    async function upload() {
    setBusy(true);
    try {
      // Generate batch ID for tracking
      const batchId = crypto.randomUUID();
      
      // Insert into bank_transactions via Backend API (SQLite)
      const transactions = preview.map(tx => ({
        booking_date: tx.transaction_date,
        amount: tx.amount,
        sender_name: tx.sender_name,
        sender_iban: tx.sender_iban,
        reference: tx.reference,
        usage: tx.reference,
        source: "csv"
      }));

      const API_URL = import.meta.env.VITE_API_URL || 'https://api.clara360.de';
      const res = await fetch(`${API_URL}/api/bank/transactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(transactions)
      });

      const data = await res.json();
      if (!res.ok) {
        console.error("Upload failed", data);
        throw new Error(data.error || 'Upload failed');
      } else {
        console.log("Upload success", data);
      }

      // Smart Feedback
      const matched = data.matched || 0;
      const reviewNeeded = data.reviewNeeded || 0;
      const inserted = data.inserted || 0;
      
      let message = `✅ ${inserted} Transaktionen verarbeitet
`;
      if (matched > 0) message += `🟢 ${matched} automatisch zugeordnet
`;
      if (reviewNeeded > 0) message += `🟡 ${reviewNeeded} benötigen Prüfung`;
      
      alert(message);
      
      onSuccess?.();
      onClose();
      
      // Redirect to review queue if needed
      if (reviewNeeded > 0) {
        window.location.href = '/mieteingangskontrolle';
      }
    } catch (e) {
      console.error('Upload error:', e);
      alert(e?.message || 'Upload fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  }

  // ============================================================
  // RENDER
  // ============================================================
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            📤 Kontoauszug hochladen (CSV)
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-2xl"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 overflow-auto flex-1">
          
          {/* File Input */}
          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
            <input
              type="file"
              accept=".csv,text/csv"
              disabled={busy}
              onChange={(e) => e.target.files?.[0] && parseFile(e.target.files[0])}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900 dark:file:text-blue-300"
            />
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              CSV-Datei von deiner Bank (Sparkasse, Volksbank, VR Bank, etc.)
            </p>
          </div>

          {/* Summary */}
          {preview.length > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 space-y-2">
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                📊 Gesamt: <strong>{summary.total} Transaktionen</strong> | Summe: <strong>{summary.sum.toFixed(2)} €</strong>
              </div>
              
              {/* PHASE 1 FIX: Klarere Kommunikation */}
              {summary.total > PREVIEW_LIMIT && (
                <div className="text-sm text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/40 rounded p-2">
                  <div className="flex items-start gap-2">
                    <span className="text-lg">ℹ️</span>
                    <div>
                      <div>Preview zeigt die ersten <strong>{PREVIEW_LIMIT}</strong> von <strong>{summary.total}</strong> Transaktionen</div>
                      <div className="font-semibold mt-1">
                        ✅ Beim Speichern werden ALLE {summary.total} Transaktionen importiert
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Errors */}
          {errors.length > 0 && (
            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
              <div className="text-sm text-red-600 dark:text-red-400">
                ⚠️ {errors.length} Zeilen konnten nicht erkannt werden (zeige max. 10)
              </div>
              <div className="mt-2 text-xs text-red-500 dark:text-red-400 space-y-1">
                {errors.slice(0, 10).map((err, idx) => (
                  <div key={idx}>Zeile {err.row}: {err.error}</div>
                ))}
              </div>
            </div>
          )}

          {/* Preview Table */}
          {preview.length > 0 && (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <div className="max-h-96 overflow-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50 dark:bg-gray-700">
                    <tr className="text-left">
                      <th className="p-3 font-semibold">Datum</th>
                      <th className="p-3 font-semibold">Betrag</th>
                      {/* PHASE 1 FIX: Kontoinhaber prominent */}
                      <th className="p-3 font-semibold text-blue-600 dark:text-blue-400">
                        👤 Kontoinhaber
                      </th>
                      <th className="p-3 font-semibold text-xs">IBAN</th>
                      <th className="p-3 font-semibold">Verwendungszweck</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.slice(0, PREVIEW_LIMIT).map((r, idx) => (
                      <tr key={idx} className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="p-3 whitespace-nowrap">{r.transaction_date}</td>
                        <td className="p-3 font-semibold whitespace-nowrap">
                          <span className={r.amount > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                            {r.amount > 0 ? '+' : ''}{Number(r.amount).toFixed(2)} €
                          </span>
                        </td>
                        {/* PHASE 1 FIX: Name hervorgehoben, nie "–" */}
                        <td className="p-3 font-medium text-gray-900 dark:text-white">
                          {r.sender_name || <span className="text-gray-400 italic">Nicht angegeben</span>}
                        </td>
                        <td className="p-3 font-mono text-xs text-gray-600 dark:text-gray-400">
                          {r.sender_iban || '-'}
                        </td>
                        <td className="p-3 text-gray-700 dark:text-gray-300 min-w-[300px] max-w-[500px]">
                          <div className="line-clamp-2" title={r.reference}>
                            {r.reference || '-'}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {preview.length > 0 && (
              <span>
                {summary.total > PREVIEW_LIMIT 
                  ? `Alle ${summary.total} Transaktionen werden importiert` 
                  : `${preview.length} Transaktionen bereit`}
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={busy}
              className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              Abbrechen
            </button>
            <button
              onClick={upload}
              disabled={busy || preview.length === 0}
              className="px-6 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
            >
              {busy ? '⏳ Importiere...' : `✅ ${summary.total} Transaktionen speichern`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
